import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Boxes, Coins, CreditCard, Layers, Package, ShoppingCart, Wallet } from "lucide-react";
import { CompactStatCard, SideTabsPage, type SideTab } from "@/components/shared";
import { ChannelOrdersReport } from "@/components/distributor/reports";
import { channelOrdersService, type ChannelOrder } from "@/services/channelOrdersService";
import { channelStockService } from "@/services/channelStockService";
import { useChannelDesk } from "@/lib/channel-desk";

const sum = (orders: ChannelOrder[], pick: (order: ChannelOrder) => number) =>
  orders.reduce((total, order) => total + pick(order), 0);

const DistributorReports = () => {
  const desk = useChannelDesk();
  const { reportId } = useParams<{ reportId?: string }>();

  const incoming = useQuery({
    queryKey: ["channel", "orders", "incoming", "party"],
    queryFn: () => channelOrdersService.incoming(),
  });
  const outgoing = useQuery({
    queryKey: ["channel", "orders", "outgoing"],
    queryFn: () => channelOrdersService.outgoing(),
  });
  const stock = useQuery({ queryKey: ["channel", "stock"], queryFn: () => channelStockService.getMine() });

  const tabs = useMemo<SideTab[]>(() => {
    const outgoingOrders = outgoing.data ?? [];
    const incomingOrders = incoming.data ?? [];
    const approvedOut = outgoingOrders.filter((order) => order.statusText === "Approved");
    const approvedIn = incomingOrders.filter((order) => order.statusText === "Approved");
    const bvInStock = (stock.data ?? []).reduce((total, row) => total + row.quantity * (row.businessVolume ?? 0), 0);

    return [
      {
        id: "sales",
        label: desk.salesTitle,
        icon: ShoppingCart,
        render: () => (
          <ChannelOrdersReport
            title={desk.salesTitle}
            description="Approved orders you fulfilled, with the commission you earned."
            fileName="sales-report"
            orders={approvedOut}
            isLoading={outgoing.isLoading}
            stats={
              <>
                <CompactStatCard title="Approved orders" value={approvedOut.length} icon={ShoppingCart} />
                <CompactStatCard title="Units sold" value={sum(approvedOut, (o) => o.quantity)} icon={Package} />
                <CompactStatCard
                  title="Commission earned"
                  value={`₹${sum(approvedOut, (o) => o.commissionAmount).toLocaleString("en-IN")}`}
                  icon={Coins}
                  iconClassName="bg-gradient-primary"
                />
                <CompactStatCard title="BV sold" value={sum(approvedOut, (o) => o.businessVolume ?? 0)} icon={Layers} />
              </>
            }
          />
        ),
      },
      {
        id: "purchases",
        label: desk.purchaseTitle,
        icon: Boxes,
        render: () => (
          <ChannelOrdersReport
            title={desk.purchaseTitle}
            description="Approved orders that brought stock to you."
            fileName="purchases-report"
            orders={approvedIn}
            isLoading={incoming.isLoading}
            stats={
              <>
                <CompactStatCard title="Approved orders" value={approvedIn.length} icon={ShoppingCart} />
                <CompactStatCard title="Units received" value={sum(approvedIn, (o) => o.quantity)} icon={Package} />
                <CompactStatCard title="BV received" value={sum(approvedIn, (o) => o.businessVolume ?? 0)} icon={Layers} />
                <CompactStatCard title="BV still in stock" value={bvInStock} icon={Boxes} />
              </>
            }
          />
        ),
      },
      {
        id: "payments",
        label: desk.paymentTitle,
        icon: CreditCard,
        render: () => (
          <ChannelOrdersReport
            title={desk.paymentTitle}
            description="Orders grouped by how they are paid, with received and pending counts."
            fileName="payments-report"
            orders={outgoingOrders}
            isLoading={outgoing.isLoading}
            statColumns={3}
            stats={
              <>
                {["P2P", "Cash", "Online"].map((method) => {
                  const group = outgoingOrders.filter((order) => order.paymentMethodText === method);
                  const received = group.filter((order) => order.paymentStatusText === "Received").length;
                  const pending = group.filter((order) => order.paymentStatusText === "Pending").length;
                  return (
                    <CompactStatCard
                      key={method}
                      title={`${method} · received ${received} / pending ${pending}`}
                      value={`${group.length} orders`}
                      icon={Wallet}
                    />
                  );
                })}
              </>
            }
          />
        ),
      },
    ];
  }, [desk, incoming.data, incoming.isLoading, outgoing.data, outgoing.isLoading, stock.data]);

  return (
    <SideTabsPage
      title="Reports"
      description="Sales, purchases and payments. Export to Excel or PDF."
      basePath={`${desk.base}/reports`}
      tabs={tabs}
      tabId={reportId}
    />
  );
};

export default DistributorReports;
